import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import BulkUpload from '../components/common/BulkUpload';
import { useAuth } from '../context/AuthContext';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const outcomeColors = { won: 'success', lost: 'error', neutral: 'neutral' };

const emptyCompetitorForm = () => ({ name: '', description: '', products: '', strengths: '', weaknesses: '', website: '' });
const emptyMentionForm = () => ({
  competitor_id: '',
  customer_id: '',
  mention_date: new Date().toISOString().split('T')[0],
  context: '',
  outcome: 'neutral',
  notes: '',
});

export default function Competitors() {
  const { isManager } = useAuth();

  const [tab, setTab] = useState('competitors');
  const [competitors, setCompetitors] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [compModal, setCompModal] = useState({ open: false, editing: null, form: emptyCompetitorForm(), saving: false, error: '' });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [mentionModal, setMentionModal] = useState({ open: false, form: emptyMentionForm(), saving: false, error: '' });

  const loadCompetitors = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/competitors');
      setCompetitors(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMentions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/competitors/mentions');
      setMentions(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompetitors();
    api.get('/customers').then(r => setCustomers(r.data.data || r.data || [])).catch(() => {});
  }, [loadCompetitors]);

  useEffect(() => {
    if (tab === 'mentions') loadMentions();
  }, [tab, loadMentions]);

  const handleSaveCompetitor = async () => {
    const { form, editing } = compModal;
    if (!form.name) {
      setCompModal(m => ({ ...m, error: 'Name is required.' }));
      return;
    }
    setCompModal(m => ({ ...m, saving: true, error: '' }));
    try {
      if (editing) {
        await api.put(`/competitors/${editing.id}`, form);
      } else {
        await api.post('/competitors', form);
      }
      setCompModal({ open: false, editing: null, form: emptyCompetitorForm(), saving: false, error: '' });
      loadCompetitors();
    } catch (err) {
      setCompModal(m => ({ ...m, saving: false, error: err.response?.data?.message || 'Failed to save competitor.' }));
    }
  };

  const handleAddMention = async () => {
    const { form } = mentionModal;
    if (!form.competitor_id || !form.mention_date) {
      setMentionModal(m => ({ ...m, error: 'Competitor and date are required.' }));
      return;
    }
    setMentionModal(m => ({ ...m, saving: true, error: '' }));
    try {
      await api.post('/competitors/mentions', { ...form, customer_id: form.customer_id || undefined });
      setMentionModal({ open: false, form: emptyMentionForm(), saving: false, error: '' });
      loadCompetitors();
      if (tab === 'mentions') loadMentions();
    } catch (err) {
      setMentionModal(m => ({ ...m, saving: false, error: err.response?.data?.message || 'Failed to log mention.' }));
    }
  };

  const openEdit = (comp) => {
    setCompModal({
      open: true,
      editing: comp,
      form: {
        name: comp.name || '',
        description: comp.description || '',
        products: comp.products || '',
        strengths: comp.strengths || '',
        weaknesses: comp.weaknesses || '',
        website: comp.website || '',
      },
      saving: false,
      error: '',
    });
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Competitor Tracking</h1>
          <p className="page-subtitle">Monitor competitors and log field mentions</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isManager && tab === 'competitors' && (
            <>
              <button className="btn btn-secondary" onClick={() => setBulkOpen(true)}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Import
              </button>
              <button className="btn btn-primary" onClick={() => setCompModal({ open: true, editing: null, form: emptyCompetitorForm(), saving: false, error: '' })}>
                + Add Competitor
              </button>
            </>
          )}
          {tab === 'mentions' && (
            <button className="btn btn-primary" onClick={() => setMentionModal({ open: true, form: emptyMentionForm(), saving: false, error: '' })}>
              + Log Mention
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid var(--border)' }}>
        {[{ key: 'competitors', label: 'Competitors' }, { key: 'mentions', label: 'Mentions' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px', border: 'none', background: 'transparent', cursor: 'pointer',
              fontWeight: tab === t.key ? 700 : 500, fontSize: '0.875rem',
              color: tab === t.key ? 'var(--primary)' : 'var(--text-secondary)',
              borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom: -2,
            }}>{t.label}</button>
        ))}
      </div>

      {/* Competitors Cards Tab */}
      {tab === 'competitors' && (
        <>
          {loading && <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div>}
          {!loading && competitors.length === 0 && (
            <div className="card"><div className="empty-state"><p>No competitors tracked yet. Add one to get started.</p></div></div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {!loading && competitors.map(comp => (
              <div key={comp.id} className="card" style={{ padding: 0 }}>
                <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>{comp.name}</div>
                      {comp.website && (
                        <a href={comp.website.startsWith('http') ? comp.website : `https://${comp.website}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: '0.75rem', color: 'var(--primary)', textDecoration: 'none' }}>
                          {comp.website}
                        </a>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: 12, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>
                        {comp.mention_count || 0} mentions
                      </span>
                      {isManager && (
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openEdit(comp)}
                          title="Edit">
                          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Win/Loss */}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span style={{ background: '#d1fae5', color: '#059669', borderRadius: 8, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600 }}>
                      Won: {comp.wins || 0}
                    </span>
                    <span style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 8, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600 }}>
                      Lost: {comp.losses || 0}
                    </span>
                  </div>
                  {comp.description && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{comp.description}</div>
                  )}
                  {comp.products && (
                    <div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Products</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', background: 'var(--surface-2)', borderRadius: 6, padding: '6px 10px' }}>{comp.products}</div>
                    </div>
                  )}
                  {(comp.strengths || comp.weaknesses) && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {comp.strengths && (
                        <div>
                          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Strengths</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{comp.strengths}</div>
                        </div>
                      )}
                      {comp.weaknesses && (
                        <div>
                          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Weaknesses</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{comp.weaknesses}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Mentions Tab */}
      {tab === 'mentions' && (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Competitor</th>
                  <th>Customer</th>
                  <th>Context</th>
                  <th>Outcome</th>
                  <th>Mentioned By</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6}><div className="loading-overlay"><div className="spinner" /></div></td></tr>}
                {!loading && mentions.length === 0 && (
                  <tr><td colSpan={6}><div className="empty-state"><p>No mentions logged yet.</p></div></td></tr>
                )}
                {!loading && mentions.map(m => (
                  <tr key={m.id}>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{fmtDate(m.mention_date)}</td>
                    <td style={{ fontWeight: 600 }}>{m.competitor_name}</td>
                    <td>{m.customer_name || '—'}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)', fontSize: '0.83rem' }}>
                      {m.context || '—'}
                    </td>
                    <td>
                      <span className={`badge badge-${outcomeColors[m.outcome] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>
                        {m.outcome}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{m.mentioned_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Competitor Modal */}
      <Modal
        open={compModal.open}
        onClose={() => !compModal.saving && setCompModal({ open: false, editing: null, form: emptyCompetitorForm(), saving: false, error: '' })}
        title={compModal.editing ? `Edit: ${compModal.editing.name}` : 'Add Competitor'}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setCompModal({ open: false, editing: null, form: emptyCompetitorForm(), saving: false, error: '' })} disabled={compModal.saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSaveCompetitor} disabled={compModal.saving}>
            {compModal.saving ? 'Saving…' : (compModal.editing ? 'Update' : 'Add Competitor')}
          </button>
        </>}
      >
        {compModal.error && (
          <div style={{ background: 'var(--error-bg, #fef2f2)', color: 'var(--error, #dc2626)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.875rem' }}>
            {compModal.error}
          </div>
        )}
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label className="form-label">Name <span style={{ color: 'var(--error)' }}>*</span></label>
            <input className="form-control" placeholder="Competitor name"
              value={compModal.form.name}
              onChange={e => setCompModal(m => ({ ...m, form: { ...m.form, name: e.target.value } }))} />
          </div>
          <div>
            <label className="form-label">Website</label>
            <input className="form-control" placeholder="https://example.com"
              value={compModal.form.website}
              onChange={e => setCompModal(m => ({ ...m, form: { ...m.form, website: e.target.value } }))} />
          </div>
          <div>
            <label className="form-label">Description</label>
            <textarea className="form-control" rows={2}
              value={compModal.form.description}
              onChange={e => setCompModal(m => ({ ...m, form: { ...m.form, description: e.target.value } }))} />
          </div>
          <div>
            <label className="form-label">Products They Sell</label>
            <textarea className="form-control" rows={2} placeholder="List their main products…"
              value={compModal.form.products}
              onChange={e => setCompModal(m => ({ ...m, form: { ...m.form, products: e.target.value } }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Strengths</label>
              <textarea className="form-control" rows={3}
                value={compModal.form.strengths}
                onChange={e => setCompModal(m => ({ ...m, form: { ...m.form, strengths: e.target.value } }))} />
            </div>
            <div>
              <label className="form-label">Weaknesses</label>
              <textarea className="form-control" rows={3}
                value={compModal.form.weaknesses}
                onChange={e => setCompModal(m => ({ ...m, form: { ...m.form, weaknesses: e.target.value } }))} />
            </div>
          </div>
        </div>
      </Modal>

      {/* Log Mention Modal */}
      <Modal
        open={mentionModal.open}
        onClose={() => !mentionModal.saving && setMentionModal({ open: false, form: emptyMentionForm(), saving: false, error: '' })}
        title="Log Competitor Mention"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setMentionModal({ open: false, form: emptyMentionForm(), saving: false, error: '' })} disabled={mentionModal.saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAddMention} disabled={mentionModal.saving}>
            {mentionModal.saving ? 'Saving…' : 'Log Mention'}
          </button>
        </>}
      >
        {mentionModal.error && (
          <div style={{ background: 'var(--error-bg, #fef2f2)', color: 'var(--error, #dc2626)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.875rem' }}>
            {mentionModal.error}
          </div>
        )}
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label className="form-label">Competitor <span style={{ color: 'var(--error)' }}>*</span></label>
            <select className="form-control form-select"
              value={mentionModal.form.competitor_id}
              onChange={e => setMentionModal(m => ({ ...m, form: { ...m.form, competitor_id: e.target.value } }))}>
              <option value="">Select competitor</option>
              {competitors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Customer</label>
              <select className="form-control form-select"
                value={mentionModal.form.customer_id}
                onChange={e => setMentionModal(m => ({ ...m, form: { ...m.form, customer_id: e.target.value } }))}>
                <option value="">Select customer (optional)</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Date <span style={{ color: 'var(--error)' }}>*</span></label>
              <input className="form-control" type="date"
                value={mentionModal.form.mention_date}
                onChange={e => setMentionModal(m => ({ ...m, form: { ...m.form, mention_date: e.target.value } }))} />
            </div>
          </div>
          <div>
            <label className="form-label">Outcome</label>
            <select className="form-control form-select"
              value={mentionModal.form.outcome}
              onChange={e => setMentionModal(m => ({ ...m, form: { ...m.form, outcome: e.target.value } }))}>
              <option value="neutral">Neutral</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
          </div>
          <div>
            <label className="form-label">Context</label>
            <textarea className="form-control" rows={3} placeholder="What was the context? Where was the competitor mentioned?"
              value={mentionModal.form.context}
              onChange={e => setMentionModal(m => ({ ...m, form: { ...m.form, context: e.target.value } }))} />
          </div>
          <div>
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2}
              value={mentionModal.form.notes}
              onChange={e => setMentionModal(m => ({ ...m, form: { ...m.form, notes: e.target.value } }))} />
          </div>
        </div>
      </Modal>

      <BulkUpload
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Import Competitors"
        uploadUrl="/competitors/bulk"
        columns={[
          { key: 'name', label: 'Name', required: true },
          { key: 'description', label: 'Description' },
          { key: 'products', label: 'Products' },
          { key: 'strengths', label: 'Strengths' },
          { key: 'weaknesses', label: 'Weaknesses' },
          { key: 'website', label: 'Website' },
        ]}
        onSuccess={loadCompetitors}
      />
    </div>
  );
}
